import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertOctagon, CheckCircle2, XCircle, Trash2, RefreshCw } from "lucide-react";

/**
 * Founder / BDOM cancellation dashboard.
 *
 * Two tables side by side (stacked on mobile):
 *  • Open cancellation requests (partners hitting "Request cancellation" on
 *    locked rows) — Founder can mark Resolved (they've actioned the cancel
 *    themselves) or Dismiss (participant is coming after all).
 *  • Recent application deletion log (auto-populated by
 *    /partner-portal/applications/{aid} DELETE) — pure audit view, no
 *    actions, so Founder can spot trends like a spike of "Visa refused".
 */
export default function FounderCancellations() {
  const [requests, setRequests] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [r, d] = await Promise.all([
        api.get("/cancellation-requests"),
        api.get("/application-deletion-log"),
      ]);
      setRequests(r.data || []);
      setLog(d.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const openRequests = useMemo(() => requests.filter(r => r.status === "open"), [requests]);
  const closedRequests = useMemo(() => requests.filter(r => r.status !== "open"), [requests]);

  const resolve = async (r, status) => {
    try {
      await api.patch(`/cancellation-requests/${r.id}`, { status });
      toast.success(status === "resolved" ? "Marked resolved" : "Dismissed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="founder-cancellations">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-red-700 font-bold flex items-center gap-1.5">
            <AlertOctagon size={12}/> Cancellations
          </div>
          <h2 className="font-display font-semibold text-lg">Cancellation requests &amp; deletion log</h2>
          <p className="text-xs text-[color:var(--aii-muted)]">
            Partners can no longer nuke a booked participant — they raise a ticket here instead. Every self-delete (pending / rejected rows) is auto-logged so you can spot cancellation trends.
          </p>
        </div>
        <button onClick={load} disabled={busy} data-testid="cxl-refresh"
                className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
          <RefreshCw size={11}/> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Open cancellation requests */}
        <div className="aii-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold flex items-center gap-1.5">
              <AlertOctagon size={13} className="text-red-600"/> Open requests
            </div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-bold uppercase border border-red-200" data-testid="cxl-open-count">
              {openRequests.length}
            </span>
          </div>
          {openRequests.length === 0 ? (
            <div className="text-sm text-[color:var(--aii-muted)] italic py-4 text-center" data-testid="cxl-empty">
              No open cancellation requests.
            </div>
          ) : (
            <ul className="space-y-2">
              {openRequests.map(r => (
                <li key={r.id} data-testid={`cxl-req-${r.id}`}
                    className="border border-red-200 bg-red-50/40 rounded-md p-3 text-sm">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{r.participant_name || "—"}</div>
                      <div className="text-[11px] text-[color:var(--aii-muted)]">
                        {[r.partner_name, r.program, r.destination, r.city, r.start_date].filter(Boolean).join(" · ")}
                      </div>
                      {r.reason && (
                        <div className="mt-1 text-[11px] text-[color:var(--aii-text-2)] italic">&ldquo;{r.reason}&rdquo;</div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button onClick={() => resolve(r, "resolved")}
                              data-testid={`cxl-resolve-${r.id}`}
                              className="text-[10px] px-2 py-1 border border-emerald-600 text-emerald-700 rounded hover:bg-emerald-50 font-semibold flex items-center gap-1">
                        <CheckCircle2 size={10}/> Resolve
                      </button>
                      <button onClick={() => resolve(r, "dismissed")}
                              data-testid={`cxl-dismiss-${r.id}`}
                              className="text-[10px] px-2 py-1 border border-[color:var(--aii-border)] text-[color:var(--aii-muted)] rounded hover:bg-slate-50 font-semibold flex items-center gap-1">
                        <XCircle size={10}/> Dismiss
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {closedRequests.length > 0 && (
            <details className="mt-3">
              <summary className="text-[11px] text-[color:var(--aii-muted)] cursor-pointer hover:text-brand" data-testid="cxl-closed-toggle">
                Show {closedRequests.length} closed request{closedRequests.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-2 space-y-1.5">
                {closedRequests.map(r => (
                  <li key={r.id} className="text-[11px] flex items-center justify-between gap-2 border border-[color:var(--aii-border)] rounded p-2">
                    <div>
                      <span className="font-semibold">{r.participant_name}</span>
                      <span className="text-[color:var(--aii-muted)]"> · {r.partner_name}</span>
                    </div>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${r.status === "resolved" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                      {r.status}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* Deletion log */}
        <div className="aii-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold flex items-center gap-1.5">
              <Trash2 size={13} className="text-[color:var(--aii-muted)]"/> Deletion log (pending/rejected)
            </div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-bold uppercase border border-[color:var(--aii-border)]">
              {log.length}
            </span>
          </div>
          {log.length === 0 ? (
            <div className="text-sm text-[color:var(--aii-muted)] italic py-4 text-center">No deletions logged yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {log.slice(0, 20).map(l => (
                <li key={l.id} data-testid={`cxl-log-${l.id}`}
                    className="border border-[color:var(--aii-border)] rounded p-2 text-[11px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{l.participant_name || "—"}</span>
                    <span className="text-[10px] text-[color:var(--aii-muted)]">{(l.created_at || "").slice(0, 10)}</span>
                  </div>
                  <div className="text-[10px] text-[color:var(--aii-muted)]">
                    {[l.partner_name, l.program, l.destination, l.city].filter(Boolean).join(" · ")}
                  </div>
                  {l.reason && (
                    <div className="mt-0.5 text-[10px] text-[color:var(--aii-text-2)] italic">&ldquo;{l.reason}&rdquo;</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
